"use client";

import React from "react";
import { motion } from "framer-motion";
import { ShieldCheck, Layers, ArrowUpRight, PhoneCall, CircleCheck as CheckCircle2, PackageCheck, HardHat, Boxes } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
const partnershipFeatures = [{
  icon: Layers,
  title: "Custom eaves access",
  description: "Configured exactly to fascia and gutter line heights for unhindered rafter, batten, and felt replacement."
}, {
  icon: ShieldCheck,
  title: "Integrated edge protection & brick guards",
  description: "Triple-guardrail perimeters and mesh infills that stop dropped materials and protect pedestrians and ground workers below."
}, {
  icon: Boxes,
  title: "Heavy-duty loading bays",
  description: "High-capacity loading platforms engineered to bear full pallet weights of slate, clay tiles, and timber trusses."
}, {
  icon: HardHat,
  title: "Internal ladder access towers",
  description: "Dedicated internal ladder runs with safety trapdoors that preserve uncluttered walkway decks and eliminate exterior hazards."
}];
const accreditationBadges = ["NASC Compliant", "CHAS Accredited"];
function PartnershipContent() {
  const {
    posts,
    loading
  } = usePosts();
  const photoEntry = posts.find(p => (p as any).acf?.photo_name === "Roofing Workflow Image" || p.customFields?.photo_name === "Roofing Workflow Image");
  const rawImage = (photoEntry as any)?.acf?.image || photoEntry?.customFields?.image;
  const imageSrc = typeof rawImage === "string" && rawImage.trim() ? rawImage : typeof rawImage === "object" && rawImage?.url ? rawImage.url : "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1793066/img/active_reroofing_scaffold.png";
  const rawAlt = (photoEntry as any)?.acf?.alt_text || photoEntry?.customFields?.alt_text;
  const imageAlt = typeof rawAlt === "string" && rawAlt.trim() ? rawAlt : "Active re-roofing project with secure perimeter scaffolding and heavy-duty loading bay";
  const isFallback = imageSrc === "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1793066/img/active_reroofing_scaffold.png";
  return <section id="roofing-contractor-partnership" data-nav="light" className="bg-background text-foreground py-16 sm:py-20 lg:py-28 border-b border-border overflow-hidden">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
        {/* Header Block */}
        <motion.div initial={{
        opacity: 0,
        y: 18
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        amount: 0.3
      }} transition={{
        duration: 0.45,
        ease: [0.16, 1, 0.3, 1]
      }} className="max-w-3xl mb-12 lg:mb-16">
          <div className="flex items-center gap-3 mb-4">
            <span className="w-8 h-[3px] bg-primary rounded-full" />
            <span className="text-xs sm:text-sm font-bold uppercase tracking-wider text-muted-foreground">
              Contractor Partnerships
            </span>
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground font-default">
            Scaffolding built around roofing workflows
          </h1>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
            We deliver reliable eaves access, perimeter edge protection, and high-capacity loading bays so your roofing teams can install tiles, leadwork, and trusses safely without site hold-ups. BMF aims to become the scaffolding partner roofing contractors can rely on job after job.
          </p>
        </motion.div>

        {/* Main Content Grid: Image + Feature Modules */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
          {/* Left Column: Documentary Project Image & Accreditation strip */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          amount: 0.2
        }} transition={{
          duration: 0.45,
          ease: [0.16, 1, 0.3, 1]
        }} className="lg:col-span-6 space-y-6">
            <div className="relative rounded-lg overflow-hidden border border-border bg-muted shadow-sm group">
              {loading ? <div className="w-full aspect-[4/3] bg-muted animate-pulse" /> : <img src={imageSrc} data-wvc-srcset={isFallback ? "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1793066/img/active_reroofing_scaffold.png-wvc-srcset" : ""} data-wvc-sizes={isFallback ? "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1793066/img/active_reroofing_scaffold.png-wvc-sizes" : ""} alt={imageAlt} loading="lazy" decoding="async" className="w-full aspect-[4/3] object-cover transition-transform duration-500 group-hover:scale-[1.02]" />}
              <div className="absolute inset-0 bg-gradient-to-t from-secondary/80 via-secondary/20 to-transparent pointer-events-none" />
              
              <div className="absolute bottom-0 inset-x-0 p-5 sm:p-6 text-secondary-foreground flex flex-col sm:flex-row sm:items-end justify-between gap-4">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-primary">Live Site Standard</p>
                  <p className="text-sm sm:text-base font-semibold text-secondary-foreground mt-0.5">
                    Continuous access platform with debris netting
                  </p>
                </div>
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-secondary/80 backdrop-blur-xs border border-border/30 text-xs font-medium text-secondary-foreground whitespace-nowrap">
                  <PackageCheck className="size-4 text-primary shrink-0" />
                  <span>Handover Tagged</span>
                </div>
              </div>
            </div>

            {/* Quick Accreditation Ribbon */}
            <div className="p-4 sm:p-5 rounded-lg border border-border bg-card shadow-xs flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div>
                <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground block mb-1">
                  Verified Compliance
                </span>
                <span className="text-sm font-semibold text-foreground">
                  TG20:21 &amp; SG4:22 Safety Audited
                </span>
              </div>
              <div className="flex flex-wrap gap-2">
                {accreditationBadges.map((badge, i) => <span key={badge} data-index={i} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-muted text-xs font-semibold text-foreground border border-border">
                    <CheckCircle2 className="size-3.5 text-primary shrink-0" />
                    {badge}
                  </span>)}
              </div>
            </div>
          </motion.div>

          {/* Right Column: Key Specifications & Partnership Features */}
          <div className="lg:col-span-6 flex flex-col justify-between gap-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {partnershipFeatures.map((feature, i) => {
              const IconComponent = feature.icon;
              return <motion.div key={feature.title} data-index={i} initial={{
                opacity: 0,
                y: 16
              }} whileInView={{
                opacity: 1,
                y: 0
              }} viewport={{
                once: true,
                amount: 0.2
              }} transition={{
                duration: 0.45,
                delay: i * 0.08,
                ease: [0.16, 1, 0.3, 1]
              }}>
                    <Card className="h-full border-border bg-card p-5 sm:p-6 transition-[border-color,box-shadow] duration-200 hover:border-primary/50 hover:shadow-md">
                      <div className="size-10 rounded-md bg-accent flex items-center justify-center text-primary mb-4 border border-border">
                        <IconComponent className="size-5" />
                      </div>
                      <h3 className="text-base sm:text-lg font-bold text-card-foreground tracking-tight font-default">
                        {feature.title}
                      </h3>
                      <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
                        {feature.description}
                      </p>
                    </Card>
                  </motion.div>;
            })}
            </div>

            {/* Direct Action Card for Roofing Contractors */}
            <motion.div initial={{
            opacity: 0,
            y: 16
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            amount: 0.2
          }} transition={{
            duration: 0.45,
            delay: 0.35,
            ease: [0.16, 1, 0.3, 1]
          }} className="p-6 sm:p-7 rounded-lg border border-border bg-secondary text-secondary-foreground shadow-sm">
              <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
                <div>
                  <h4 className="text-lg font-bold text-secondary-foreground font-default">
                    Partner with BMF on upcoming tenders
                  </h4>
                  <p className="mt-1 text-sm text-secondary-foreground/80">
                    We quote from roof plans, elevations, or direct site surveys across the South East.
                  </p>
                </div>
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full md:w-auto shrink-0">
                  <Button asChild size="default" className="bg-primary text-primary-foreground hover:bg-primary/90">
                    <Link to="/ContactRequestQuotePage" className="inline-flex items-center justify-center gap-2">
                      <span>Request a quote</span>
                      <ArrowUpRight className="size-4" />
                    </Link>
                  </Button>
                  <Button asChild variant="outline" size="default" className="border-secondary-foreground/20 text-secondary-foreground hover:bg-secondary-foreground/10 hover:text-secondary-foreground bg-transparent">
                    <Link to="/ContactRequestQuotePage" className="inline-flex items-center justify-center gap-2">
                      <PhoneCall className="size-4 text-primary" />
                      <span>Discuss a project</span>
                    </Link>
                  </Button>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>;
}
export default function Partnership() {
  const wp_query = new WP_Query({
    post_type: "site_photo",
    per_page: 100,
    status: ["publish"]
  });
  return <PostsProvider wp_query={{
    post_type: "site_photo",
    per_page: 100,
    status: ["publish"]
  }}>
      <PartnershipContent />
    </PostsProvider>;
}